import React, { useState, useEffect } from "react";

import Popup from "./Popup";
import { Search } from "react-feather";
import { CFormCheck, CAvatar } from "@coreui/react";

import axiosInstance from "../apis/gvmdesk";
import useAxiosFunction from "../hooks/useAxiosFunction";
import LoadingCenter from "./LoadingCenter";
import { useMutation } from "@tanstack/react-query";
import { useHttp } from "../hooks/useHttp";
import openToast from "../utils/sweetAlert";
import { formatAdminTransferLabel } from "../utils/porte";

const PopupTransfer = props => {
  const { admin } = props;
  const [search, setSearch] = useState("");
  const [response, setResponse, loading, error, axiosFetch] = useAxiosFunction();
  const [contacts, setContacts] = useState(null);
  const [contactsCount, setContactsCount] = useState(null);
  const [visible, setVisible] = useState(false);
  const [targetAdmin, setTargetAdmin] = useState("");
  const [adminOptions, setAdminOptions] = useState([]);
  const [adminOptionsLoading, setAdminOptionsLoading] = useState(false);
  const request = useHttp();

  const clearVariables = () => {
    setSearch("");
    setResponse(null);
    setContacts(null);
    setContactsCount(0);
    setVisible(false);
    setTargetAdmin("");
    setAdminOptions([]);
  }

  useEffect(() => {
    clearVariables();
  }, [props.trigger]);

  useEffect(() => {
    if (props.trigger && admin) {
      axiosFetch({
        axiosInstance,
        method: "GET",
        url: "/users/admin/all/by/" + admin.id,
        redirect_to: ""
      });
    }
  }, [props.trigger, admin]);

  useEffect(() => {
    const fetchAdmins = async () => {
      if (!props.trigger || !admin) return;

      try {
        setAdminOptionsLoading(true);
        const response = await request({ url: "/admins/list?sales_only=1", method: "GET" });
        const admins = Array.isArray(response?.data) ? response.data : [];

        setAdminOptions(admins.filter(item => Number(item.id) !== Number(admin.id)));
      } catch (error) {
        openToast("error", "Ocorreu um erro", error.response?.data?.message || "Não foi possível listar os admins.");
      } finally {
        setAdminOptionsLoading(false);
      }
    };

    fetchAdmins();
  }, [props.trigger, admin]);

  useEffect(() => {
    setContacts(response?.contacts?.map(contact => {
      return {
        ...contact,
        checked: true
      };
    }));
  }, [response?.contacts]);

  useEffect(() => {
    if (contacts?.length > 0) {
      let selectedContacts = contacts?.filter(contact => contact.checked == true);

      setVisible(selectedContacts.length > 0);
      setContactsCount(selectedContacts.length);
    } else {
      setVisible(false);
      setContactsCount(0);
    }
  }, [contacts]);

  const onChangeCheck = (event, contact) => {
    let { checked } = event.target;

    setContacts(contacts.map(oldContact => {
      if (contact.id === oldContact.id) {
        return {
          ...oldContact,
          checked
        };
      }

      return oldContact;
    }));
  }

  const onChangeAll = event => {
    let { checked } = event.target;

    setContacts(contacts.map(contact => ({
      ...contact,
      checked
    })));
  }

  const avatarStyle = avatarPicture => ({
    width: "45px",
    height: "45px",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundImage: `url(${avatarPicture})`
  });

  const transferMutation = useMutation({
    mutationKey: ["transfer-vacation-customers"],
    mutationFn: ({ users, adminId }) => request({
      url: "/users/transferirdemissao",
      method: "POST",
      data: {
        users_id: users,
        new_admin_id: adminId
      }
    }),
    onSuccess: response => {
      if (response?.data?.error) {
        openToast("error", "Ocorreu um erro", response.data.message);
        return;
      }

      openToast("success", "Sucesso", response?.data?.message || "Clientes transferidos com sucesso.");
      props.setTrigger(false);
      props.onSuccess?.();
    },
    onError: error => {
      openToast("error", "Ocorreu um erro", error.response?.data?.message || "Não foi possível transferir os clientes.");
    }
  });

  const onSubmit = (event) => {
    event.preventDefault();

    let selectedContacts = contacts?.filter(contact => contact.checked == true) || [];

    if (selectedContacts.length <= 0) {
      openToast("error", "Ocorreu um erro", "Selecione pelo menos um cliente.");
      return;
    }

    if (!targetAdmin) {
      openToast("error", "Ocorreu um erro", "Selecione o admin destino.");
      return;
    }

    transferMutation.mutate({
      users: selectedContacts.map(contact => contact.id),
      adminId: targetAdmin
    });
  }

  const filteredConsumers = Array.isArray(contacts)
    ? contacts.filter(contact => contact.name?.toLowerCase().includes(search.toLowerCase()))
    : [];

  return (
    <Popup title={`Transferir clientes de ${admin && admin.name}`} trigger={props.trigger} setTrigger={props.setTrigger} >
      <form onSubmit={onSubmit}>
        <div className="x-p-20 y-p-20">
          <div className="inner-search">
            <div className="search-icon">
              <Search size={24} />
            </div>

            <input className="search-input" type="text" placeholder="Pesquisar"
              onChange={e => setSearch(e.target.value)} value={search} />
          </div>
        </div>

        {!loading && contactsCount > 0
          && <div className="x-p-20 y-p-20 pt-0 text-color">{contactsCount} {contactsCount > 1 ? "contatos selecionados" : "contato selecionado"}</div>}

        {!loading && contacts?.length > 0 && <div className="x-p-20 pb-20">
          <label className="text-color d-block mb-10">Admin destino</label>
          {adminOptionsLoading ? (
            <LoadingCenter />
          ) : (
            <select className="search-input" value={targetAdmin} onChange={event => setTargetAdmin(event.target.value)}>
              <option value="">Selecione um admin</option>
              {adminOptions.map(option => (
                <option key={option.id} value={option.id}>{formatAdminTransferLabel(option)}</option>
              ))}
            </select>
          )}
        </div>}

        <div>
          <ul className="contact__list">
            {loading && <LoadingCenter />}

            {!loading && contacts?.length <= 0 && <div className="x-p-20 y-p-10 text-color">O(a) vendedor(a) {admin.name} não possui clientes.</div>}

            {!loading && visible && <li className="contact__item x-p-20 y-p-10 d-flex flex-row align-center">
              <div className="mr-20">
                <CFormCheck
                  checked={contacts?.length > 0 && contacts.every(contact => contact.checked)}
                  onChange={onChangeAll}
                />
              </div>

              <span className="text-color">Selecionar tudo</span>
            </li>}

            {!loading && contacts?.length > 0 && filteredConsumers.length <= 0 && <div className="x-p-20 y-p-10 text-color">Nenhum cliente corresponde à busca.</div>}

            {!loading && filteredConsumers?.map((contact, index) => {
              return (
                <li key={contact.id} className="contact__item x-p-20 y-p-10 d-flex flex-row align-center">
                  <div className="mr-20">
                    <CFormCheck name="contacts" value={contact.id} checked={contact.checked} onChange={event => onChangeCheck(event, contact)} />
                  </div>

                  <div className="mr-10">
                    <CAvatar color="light" textColor="dark" style={avatarStyle(contact.profile_pic)} />
                  </div>

                  <span className="text-color">{contact.name}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {contactsCount > 0 && <div className="popup-footer x-p-20 y-p-20">
          <button className="btn btn-primary" type="submit" disabled={transferMutation.isLoading || adminOptionsLoading}>
            {transferMutation.isLoading ? "Transferindo..." : "Concluir"}
          </button>
          <button className="btn btn-light mr-20" type="button" onClick={() => props.setTrigger(false)}>Cancelar</button>
        </div>}
      </form>
    </Popup >
  );
}

export default PopupTransfer;
